import {ISymbol, ISymbolPosition} from "../../core/reel/IReelData";
import {PearlHarborSymbol, PearlHarborSymbolStates, SYMBOL_NAME} from "./PearlHarborSymbol";
import {PearlHarborGameConfig} from "../PearlHarborGameConfig";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {PearlHarbor} from "../PearlHarbor";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarborHelper} from "../utils/PearlHarborHelper";
import {PearlHarborGameAssets} from "../PearlHarborGameAssets";
import {PearlHarborSoundManager} from "../sound/PearlHarborSoundManager";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {PearlHarborSplitSymbol} from "./PearlHarborSplitSymbol";
import {BitMapMultiplier} from "../view/freespin/component/BitMapMultiplier";
import {ISymbolData, ITargetArea} from "../server/PearlHarborModel";
import {PearlHarborTargetSymbol} from "./PearlHarborTargetSymbol";
import {PearlHarborScatterSymbol} from "./PearlHarborScatterSymbol";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export interface ILandingSymInfo {
    reelTargetPosition:number;
    distance:number;
}

export class PearlHarborReelView extends PIXI.Container {
    private bigSymolView:PIXI.Container;
    private specialSymbolView:PIXI.Container;
    private _allOutSymbols:ISymbol[] = [];
    private _allInSymbols:ISymbol[] = [];
    private _collapsedReel:ISymbol[] = [];
    private _beforeSplitSymbol:ISymbol[] = [];
    private allSymPosY:number[] = [];
    private static SYMBOLS_FALL_DURATION:number = 0.4;
    private static SYMBOLS_COLLAPSE_FALL_DURATION:number = 0.08;
    private static SYMBOLS_COLLAPSE_FALL_DURATION_FAST_SPIN:number = 0.06;
    private static SYMBOLS_FALL_DURATION_FAST_SPIN:number = 0.3;
    private static SYMBOLS_UNBLUR_DURATION:number = 0.1;
    private _afterCollapseSymNames:ISymbolData[] = [];
    private readonly _reelId:number;

    public static landingSymbolArray:ILandingSymInfo[] = [];

    constructor(id:number) {
        super();
        this.bigSymolView = new PIXI.Container();
        this.specialSymbolView = new PIXI.Container();
        this.bigSymolView.name = "BigSymReel " + id;
        this.specialSymbolView.name = "SpecialReel " + id;
        this.name = "Reel " + id;
        this._reelId = id;
        this.position = PearlHarborGameConfig.reelPositions[id];
        this.bigSymolView.position = PearlHarborGameConfig.reelPositions[id];
        this.specialSymbolView.position = PearlHarborGameConfig.reelPositions[id];
        this.specialSymbolView.sortableChildren = true;
    }

    public removeAvalancheSymbol(symbol:ISymbol) {
        const sym:PearlHarborSymbol = <PearlHarborSymbol>symbol;
        if(sym.isBig) {
            this.bigSymolView.removeChild(sym);
        } else if(PearlHarborHelper.isSpecialSymbol(sym.symbolName)) {
            this.specialSymbolView.removeChild(sym);
        } else {
            this.removeChild(sym);
        }
        sym.remove();
    }

    private symbolLanding(sym:ISymbol):void {
        const symbol = <PearlHarborSymbol>sym;

        if(symbol.symbolName == SYMBOL_NAME.TARGET || symbol.symbolName == SYMBOL_NAME.TARGET_H) {
            (<PearlHarborTargetSymbol>symbol).playTargetLandingAnim();
        } else if(symbol.symbolName == SYMBOL_NAME.BOMB || symbol.symbolName == SYMBOL_NAME.BOMB_H) {
            (<PearlHarborSymbol>symbol).playOutsideIdleAnim();
        } else if(symbol.symbolName.indexOf(SYMBOL_NAME.XS) > -1) {
            (<PearlHarborSplitSymbol>symbol).playXSLandingAnim();
        } else if(symbol.symbolName == SYMBOL_NAME.SCATTER) {
            PearlHarbor.scatterAnimation.addScatter(<PearlHarborScatterSymbol>symbol);
            (<PearlHarborScatterSymbol>symbol).playScatterOutsideIdleAnim();
        }
    }

    public resetSymbolConfig(resetY:boolean = true):void {
        let reelTargetPosition:number = 14, toY:number = PearlHarborGameConfig.symbolHeight * 7;
        if(resetY) {
            this.allSymPosY = [];
        }
        const allSyms:string[] = [];

        for(let j:number = (this._allOutSymbols.length - 1); j > -1; j--) {
            const sym:PearlHarborSymbol = <PearlHarborSymbol>this._allOutSymbols[j];
            reelTargetPosition -= (sym.isSplit ? 1 : 2);
            sym.reelTargetPosition = reelTargetPosition;
            sym.reelPosition = reelTargetPosition;
            sym.onReelId = j;
            sym.reelId = this._reelId;
            sym.collapseYPos = toY;
            if(resetY) {
                toY -= sym.symHeight
                this.allSymPosY.push(toY);
            }
            allSyms.push(this._allOutSymbols[j].symbolName);
        }
    }

    private static symLandingSoundHandler(symbol:PearlHarborSymbol | ISymbol):void {
        let shouldPlay:boolean = true;
        for(let i = 0; i < PearlHarborReelView.landingSymbolArray.length; i++) {
            if(shouldPlay && PearlHarborReelView.landingSymbolArray[i].reelTargetPosition === symbol.reelTargetPosition &&
                (PearlHarborReelView.landingSymbolArray[i].distance === (symbol.reelTargetPosition - symbol.reelPosition))) {
                shouldPlay = false;
                break;
            }
        }

        if(shouldPlay) {
            PearlHarborSoundManager.playSymLandingSound(symbol.reelId);
        }

        PearlHarborReelView.landingSymbolArray.push({
            reelTargetPosition : symbol.reelTargetPosition,
            distance : symbol.reelTargetPosition - symbol.reelPosition
        });
    }

    public clearReel():void {
        while (this._allOutSymbols.length > 0) {
            const sym:ISymbol | undefined = this._allOutSymbols.pop()
            if (sym){
                this.removeAvalancheSymbol(sym)
            }
        }

        while (this._allInSymbols.length > 0) {
            const sym:ISymbol | undefined = this._allInSymbols.pop()
            if (sym){
                this.removeAvalancheSymbol(sym)
            }
        }
        while (this._collapsedReel.length > 0) {
            const sym:ISymbol | undefined = this._collapsedReel.pop()
            if (sym){
                this.removeAvalancheSymbol(sym)
            }
        }
        while (this._beforeSplitSymbol.length > 0) {
            const sym:ISymbol | undefined = this._beforeSplitSymbol.pop()
            if (sym){
                this.removeAvalancheSymbol(sym)
            }
        }

        this._afterCollapseSymNames = [];
    }

    public initReel(symbols:ISymbolData[]):void {
        let startPosition:number = 0;
        for(let i:number = 0; i < symbols.length; i++) {
            const symbol:ISymbol = this.createSymbol(symbols[i], PearlHarborSymbolStates.NORMAL, {
                reelId : this._reelId,
                startPosition : startPosition,
                targetPosition : startPosition
            }, i, true);
            if(PearlHarbor.model.isRestore && PearlHarbor.model.isNextNormal) {
                if(symbols[i].symName != "X"&& symbols[i].symName != "X_H") {
                    this._collapsedReel.push(symbol);
                }
                this._afterCollapseSymNames = PearlHarbor.model.afterCollapseSymbolsData[this._reelId].concat();
            }
            if(symbols[i].symName != "X" && symbols[i].symName != "X_H") {
                this._allOutSymbols.push(symbol);
            }
            startPosition += (PearlHarborHelper.isHalfSymbol(symbol.symbolName) ? 1 : 2);
        }
    }

    public createSymbol(symbolData:ISymbolData, state:PearlHarborSymbolStates, symbolPositionData:ISymbolPosition, atPos:number, init:boolean = false):ISymbol {
        let symbol:PearlHarborSymbol;
        const symbolName = symbolData.symName,
        isScatter:boolean = symbolName === SYMBOL_NAME.SCATTER || symbolName === SYMBOL_NAME.SCATTER_H,
        isXS:boolean = symbolName === SYMBOL_NAME.XS || symbolName === SYMBOL_NAME.XS_H,
        isBomb:boolean = symbolName === SYMBOL_NAME.BOMB_H || symbolName === SYMBOL_NAME.BOMB;
        if(PearlHarborHelper.isXS(symbolName)) {
            symbol = new PearlHarborSplitSymbol(symbolData).init(state);
        } else if(isScatter) {
            symbol = new PearlHarborScatterSymbol(symbolData).init(state);
        } else if(symbolName === SYMBOL_NAME.TARGET || symbolName === SYMBOL_NAME.TARGET_H) {
            symbol = new PearlHarborTargetSymbol(symbolData).init(state);
        } else {
            symbol = new PearlHarborSymbol(symbolData).init(state);
        }
        const initPosY:number = symbolData.isBig ? -400 : -100;
        symbol.position.set(0, init ? symbolData.collapseY : initPosY);

        symbol.reelTargetPosition = symbolPositionData.targetPosition;
        symbol.reelPosition = symbolPositionData.startPosition;
        symbol.reelId = symbolPositionData.reelId;
        symbol.sizeMultiplier = symbolData.sizeMultiplier;
        symbol.onReelId = atPos;

        if(symbolData.isBig) {
            this.bigSymolView.addChild(symbol);
        } else if(PearlHarborHelper.isSpecialSymbol(symbolName)) {
            if(isXS||isBomb){
                this.specialSymbolView.addChild(symbol);
            }else{
                this.specialSymbolView.addChildAt(symbol, 0);
            }
        } else {
            this.addChild(symbol);
        }
        return symbol;
    }

    public getAllOutAnimation():TimelineLite {
        //console.log(" getAllOutAnimation: ");
        const returnTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        let counter:number = 0;
        while(this._allOutSymbols.length > 0) {
            const symbol:PearlHarborSymbol = <PearlHarborSymbol>this._allOutSymbols.pop()!;
            if(symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateAllOut, symbol is null!`);
            } else {
                symbol.reelTargetPosition = PearlHarborGameConfig.totalSymsPerReel * 2;
                symbol.fallOut();
                timeline = gsap.timeline();
                timeline.add([
                    gsap.timeline({delay : PearlHarborReelView.SYMBOLS_UNBLUR_DURATION}).add((<PearlHarborSymbol>symbol).getStateAnimation(PearlHarborSymbolStates.NORMAL, PearlHarborReelView.SYMBOLS_UNBLUR_DURATION)),
                    gsap.to(symbol.position, {
                        duration : PearlHarborReelView.SYMBOLS_FALL_DURATION,
                        y : PearlHarborGameConfig.symbolHeight * (symbol.reelTargetPosition - 1) + 60,
                        ease : "sine.in",
                        delay : PearlHarborGameConfig.reelSpinStartSymbolDelay * counter
                    })
                ]);
                timeline.add(() => this.removeAvalancheSymbol(symbol));
                subTimelines.push(timeline);
            }
            counter++;
        }
        if(this._reelId === 0) {
            returnTimeline.add(() => PearlHarborSoundManager.playReelOut());
        }
        //this.printAllOutSymbols("all out ---------");

        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public stop(reelData:ISymbolData[]):TimelineLite {
        let targetPosition:number = 0,
            toY:number = PearlHarborGameConfig.symbolHeight * 7;
        this.allSymPosY = [];

        for(let i:number = 0; i < reelData.length; i++) {
            const symbol:ISymbol = this.createSymbol(reelData[i], PearlHarborSymbolStates.NORMAL, {
                reelId : this._reelId,
                startPosition : -((reelData.length - i) + 1),
                targetPosition : targetPosition
            }, i);
            this._allInSymbols.push(symbol);
            targetPosition += (PearlHarborHelper.isHalfSymbol(symbol.symbolName) ? 1 : 2);
            this.allSymPosY.push(toY - (<PearlHarborSymbol>symbol).symHeight);
        }
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(this.getAllInAnimation());
        return tl;
    }

    private getAllInAnimation():gsap.core.Timeline {
        const returnTimeline:gsap.core.Timeline = gsap.timeline();
        let timeline:gsap.core.Timeline;
        const subTimelines:gsap.core.Timeline[] = [];
        let counter:number = 0;
        this._allOutSymbols = [];
        let nearWinDelay:number = 0;
        //if (PearlHarbor.model.gameData.nearWinReels[this._reelId]) {
        //    nearWinDelay = PearlHarborGameConfig.reelSpinStopNearWinDelay;
        //}
        while(this._allInSymbols.length > 0) {
            const symbol:ISymbol = this._allInSymbols.pop()!;
            this._allOutSymbols.unshift(symbol);
            if(symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateAllIn, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                //#421 stop spin fast if feature buy
                const fastSpin:boolean = PearlHarbor.apiPlugin.settings.get(APISetting.FAST_SPIN, false) || PearlHarbor.model.data.wasFeatureBuy,
                    totalSpinDuration:number = fastSpin ? PearlHarborGameConfig.mainAllInTotalSpinDurationFastSpin : PearlHarborGameConfig.mainAllInTotalSpinDuration,
                    symSpinStopDuration:number = (fastSpin ? PearlHarborGameConfig.mainReelSpinStopSymbolDelayFastSpin : PearlHarborGameConfig.mainReelSpinStopSymbolDelay) + (counter * 0.01),
                    landingDelay:number = totalSpinDuration + (symSpinStopDuration + nearWinDelay);

                //Logger.logDev("anticipation delay log reel " + this._reelId + " delay is :: " + delay);
                timeline.add([
                    gsap.timeline({delay : fastSpin ? PearlHarborGameConfig.mainReelAllInBlurDelayFastSpin + (counter * 0.03) : (PearlHarborGameConfig.mainReelAllInBlurDelay + (counter * 0.01))}).add((<PearlHarborSymbol>symbol).getStateAnimation(PearlHarborSymbolStates.LANDING, 0.1)),
                    gsap.to(symbol.position, {
                        duration : totalSpinDuration,
                        y : PearlHarborGameConfig.symbolHeight * 0.5 * symbol.reelTargetPosition,
                        ease : "power2.in",
                        delay : symSpinStopDuration + nearWinDelay
                    }),
                    gsap.timeline({delay : landingDelay}).add(() => {
                        symbol.reelPosition = symbol.reelTargetPosition;
                        this.symbolLanding(symbol);
                    }),
                ]);
                if(this._allInSymbols.length === 0) {
                    timeline.add(() => {
                        PearlHarborSoundManager.playReelLandingAll(this._reelId);
                    });
                }
                subTimelines.push(timeline);
            }
            counter++;
        }
        if(this._reelId === 0) {
            returnTimeline.add(() => PearlHarborSoundManager.playReelIn());
        }
        returnTimeline.add(subTimelines);
        //if (PearlHarbor.model.gameData.nearWinReels[this._reelId + 1]) {
        //    returnTimeline.add(() => this.playScatterAnticipation());
        //}

        return returnTimeline;
    }

    private getFillUpAnimation():TimelineLite {
        const fillUpTimeline = gsap.timeline();
        let timeline:TimelineLite,
            isFirstSymbol:boolean = true;
        const subTimelines:TimelineLite[] = [];

        while(this._collapsedReel.length > 0) {
            const symbol:ISymbol = this._collapsedReel.pop()!;
            this._allOutSymbols.unshift(symbol);
            if(symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateFillUp, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                let delay:number = PearlHarborGameConfig.reelSpinStopSymbolDelay;
                const fastSpin:boolean = PearlHarbor.apiPlugin.settings.get(APISetting.FAST_SPIN, false) || PearlHarbor.model.data.wasFeatureBuy,
                    fallDuration:number = fastSpin ? PearlHarborReelView.SYMBOLS_FALL_DURATION_FAST_SPIN : PearlHarborReelView.SYMBOLS_FALL_DURATION;
                //if (PearlHarbor.model.gameData.nearWinReels[this._reelId]) {
                //    delay = (PearlHarborGameConfig.reelSpinStopNearWinDelay + PearlHarborGameConfig.reelSpinStopSymbolDelay);
                //}
                timeline.add([
                    gsap.timeline({delay : PearlHarborReelView.SYMBOLS_UNBLUR_DURATION}).add((<PearlHarborSymbol>symbol).getStateAnimation(PearlHarborSymbolStates.LANDING, PearlHarborReelView.SYMBOLS_UNBLUR_DURATION)),
                    gsap.to(symbol.position, {
                        duration : fallDuration,
                        y : (<PearlHarborSymbol>symbol).collapseYPos,
                        ease : "power2.in",
                        delay : delay
                    }),
                    gsap.timeline({delay : (PearlHarborReelView.SYMBOLS_UNBLUR_DURATION + fallDuration)}).add(() => {
                        if(isFirstSymbol && PearlHarborHelper.isActiveCell(symbol.reelId, symbol.reelTargetPosition)) {
                            PearlHarborReelView.symLandingSoundHandler(symbol);
                            PearlHarborSoundManager.playReelAvalancheLanding();
                            isFirstSymbol = false;
                        }
                        symbol.reelPosition = symbol.reelTargetPosition;
                        this.symbolLanding(symbol);
                    })
                ]);
                subTimelines.push(timeline);
            }
        }

        fillUpTimeline.add(subTimelines);
        fillUpTimeline.add(() => {
            this._allOutSymbols.sort((a:ISymbol, b:ISymbol) => {
                return (<PearlHarborSymbol>a).position.y - (<PearlHarborSymbol>b).position.y;
            });
            this.resetSymbolConfig();
        });

        //this.resetSymbolConfig();
        return fillUpTimeline;
    }

    public getFillUpReelAnimation():TimelineLite {
        this._collapsedReel = [];
        let targetPos:number = 0;
        for(let i:number = 0; i < this._afterCollapseSymNames.length; i++) {
            if(this._afterCollapseSymNames[i].symName === "X" || this._afterCollapseSymNames[i].symName === "X_H") {
                const symbol:ISymbol = this.createSymbol(PearlHarbor.model.allSymbolsData[this._reelId][i], PearlHarborSymbolStates.NORMAL, {
                    reelId : this._reelId,
                    startPosition : -6 - i,
                    targetPosition : targetPos
                }, i);
                (<PearlHarborSymbol>symbol).sizeMultiplier = 1;
                this._collapsedReel.push(symbol);
            }
            targetPos += (PearlHarborHelper.isHalfSymbol(this._afterCollapseSymNames[i].symName) ? 1 : 2);
        }

        this.resetSymbolConfig(false);
        return this.getFillUpAnimation();
    }

    private getCollapseAnimation(symPos:number[]):TimelineLite {
        const collapseTimeline:gsap.core.Timeline = gsap.timeline(),
            symNum:number = this._allOutSymbols.length,
            subTimelines:gsap.core.Timeline[] = [];
        let timeline:gsap.core.Timeline,
            isFirstSymbol:boolean = true,
            toY:number = 0;
        const allSymbols:ISymbol[] = this._allOutSymbols.concat();
        this._allOutSymbols = [];
        allSymbols.reverse();

        for(let i:number = 0; i < symNum; i++) {
            const symbol:PearlHarborSymbol = <PearlHarborSymbol>allSymbols[i];
            if(symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.collapseAnimation, symbol is null!`);
            }

            if(!symbol.isRemoved) {
                this._allOutSymbols.push(symbol);
                const fastSpin:boolean = PearlHarbor.apiPlugin.settings.get(APISetting.FAST_SPIN, false) || PearlHarbor.model.data.wasFeatureBuy;
                const duration:number = (symPos[toY] === symbol.position.y) ? 0 : ((fastSpin ? PearlHarborReelView.SYMBOLS_COLLAPSE_FALL_DURATION_FAST_SPIN : PearlHarborReelView.SYMBOLS_COLLAPSE_FALL_DURATION) * (i - toY));
                timeline = gsap.timeline();
                if(duration > 0) {
                    timeline.add([
                        gsap.to(symbol.position, {
                            duration : duration,
                            y : symbol.collapseYPos,
                            ease : "power2.in"
                        })
                    ]);
                    timeline.add(() => {
                        if(isFirstSymbol) {
                            PearlHarborReelView.symLandingSoundHandler(symbol);
                            PearlHarborSoundManager.playReelCollapseLanding();
                            isFirstSymbol = false;
                        }
                    });
                }
                subTimelines.push(timeline);
                toY++;
            }
        }

        this._allOutSymbols.reverse();
        collapseTimeline.add(subTimelines);
        if(PearlHarbor.model.afterCollapseSymbolsData[this._reelId].length < 1) {
            this._allOutSymbols.sort((a:ISymbol, b:ISymbol) => {
                return (<PearlHarborSymbol>a).collapseYPos > (<PearlHarborSymbol>b).collapseYPos ? 1 : -1;
            });
            this.resetSymbolConfig();
        }
        return collapseTimeline;
    }

    public isNotSameSym(leftSym:PearlHarborSymbol, collapseData:ISymbolData):boolean {
        let leftSymCompareName:string = leftSym.symbolName;
        let lastCollapseDataSymName:string = collapseData.symName,
            afterSymCompareName:string = lastCollapseDataSymName;
        if(lastCollapseDataSymName.substr(-4, 1) === "x") {
            //is big symbol
            afterSymCompareName = lastCollapseDataSymName.substring(lastCollapseDataSymName.indexOf("_")).slice(1);
        }
        if(leftSymCompareName != afterSymCompareName) {
            return true;
        }

        if(leftSym.isBig != collapseData.isBig) {
            return true
        }

        if(leftSym.symSize[0] != collapseData.size[0]) {
            return true
        }
        if(leftSym.symSize[1] != collapseData.size[1]) {
            return true
        }

        if(leftSym.isBig) {
            leftSym.topLeftSeed = collapseData.topLeftSeed
        }

        return false;
    }

    public collapseReel():TimelineLite {
        this._afterCollapseSymNames = PearlHarbor.model.afterCollapseSymbolsData[this._reelId].concat();
        const collapseData:ISymbolData[] = ArrayHelper.initArrayWithValues(this._afterCollapseSymNames.length, (i:number) => {
                return this._afterCollapseSymNames[i]
            }),
            symbolsLeft:ISymbol[] = ArrayHelper.initArrayWithValues(this._allOutSymbols.length, (j:number) => {
                return this._allOutSymbols[j]
            }),
            newPos:number[] = [];

        this._collapsedReel = [];

        while(symbolsLeft.length > 0) {
            let lastLeftSymbolId:number = symbolsLeft.length - 1;
            if(!symbolsLeft[lastLeftSymbolId].isRemoved) {
                let lastCollapseDataId:number = collapseData.length - 1;
                const leftSym:ISymbol = symbolsLeft[lastLeftSymbolId];

                let lastCollapseDataSymName:string = collapseData[lastCollapseDataId].symName,
                    afterSymCompareName:string = lastCollapseDataSymName;
                if(lastCollapseDataSymName.substr(-4, 1) === "x") {
                    afterSymCompareName = lastCollapseDataSymName.substring(lastCollapseDataSymName.indexOf("_")).slice(1);
                }

                while(this.isNotSameSym(<PearlHarborSymbol>leftSym, collapseData[lastCollapseDataId]) && lastCollapseDataId > 0) {
                    lastCollapseDataId--;
                    lastCollapseDataSymName = collapseData[lastCollapseDataId].symName;
                    afterSymCompareName = lastCollapseDataSymName;
                    if(lastCollapseDataSymName.substr(-4, 1) === "x") {
                        //is big symbol
                        afterSymCompareName = lastCollapseDataSymName.substring(lastCollapseDataSymName.indexOf("_")).slice(1);
                    }
                }

                (<PearlHarborSymbol>symbolsLeft[lastLeftSymbolId]).collapseYPos = collapseData[lastCollapseDataId].collapseY;
                this._collapsedReel.push(symbolsLeft[lastLeftSymbolId]);
                symbolsLeft.splice(lastLeftSymbolId, 1);
                collapseData.splice(lastCollapseDataId, 1);
            } else {
                symbolsLeft.splice(lastLeftSymbolId, 1);
            }
        }

        if(this._collapsedReel.length === this._allOutSymbols.length) {
            return new TimelineLite();
        }

        return this.getCollapseAnimation(newPos);
    }

    public getSymbol(position:number):ISymbol {
        if(position > (this._allOutSymbols.length - 1)) {
            debugger;
        }
        return this._allOutSymbols[position];
    }

    public getSymbolByTargetPosition(reelPosition:number):PearlHarborSymbol | undefined {
        for(let rowId:number = 0; rowId < this._allOutSymbols.length; rowId++) {
            const currentReelTargetPosition:number = this._allOutSymbols[rowId].reelTargetPosition;
            if(currentReelTargetPosition == reelPosition) {
                return <PearlHarborSymbol>this._allOutSymbols[rowId];
            } else if(!PearlHarborHelper.isHalfSymbol(this._allOutSymbols[rowId].symbolName)) {
                if((currentReelTargetPosition + 1) == reelPosition) {
                    return <PearlHarborSymbol>this._allOutSymbols[rowId];
                }
            }
        }
        debugger;

        return undefined;
    }

    public getTargetSymbol():PearlHarborSymbol | undefined {
        for(let rowId:number = 0; rowId < this._allOutSymbols.length; rowId++) {
            if(this._allOutSymbols[rowId].symbolName.indexOf("TARGET") > -1) {
                return <PearlHarborSymbol>this._allOutSymbols[rowId];
            }
        }
        debugger;

        return undefined;
    }

    public getSymbolOnReelIdByTopLeftSeed(topLeftSeed:number):number {
        for(let rowId:number = 0; rowId < this._allOutSymbols.length; rowId++) {
            if((<PearlHarborSymbol>this._allOutSymbols[rowId]).isBig) {
                if((<PearlHarborSymbol>this._allOutSymbols[rowId]).topLeftSeed === topLeftSeed) {
                    return rowId;
                }
            }
        }
        Logger.logDev("to find sym on " + this._reelId + " with seed: " + topLeftSeed);
        for(let rowId:number = 0; rowId < this._allOutSymbols.length; rowId++) {
            Logger.logDev("sym seed:" + (<PearlHarborSymbol>this._allOutSymbols[rowId]).topLeftSeed);
        }
        debugger;
        return -1;
    }

    public multiplierShineFlyAnimation(symbol:PearlHarborSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const mulShineSpine:BitMapMultiplier = new BitMapMultiplier(PearlHarborGameAssets.BITMAP_FONT_MULTIPLIER, 30, "x");
        mulShineSpine.value = symbol.sizeMultiplier;
        mulShineSpine.alpha = 0;
        mulShineSpine.position.set((symbol.reelId + 0.5) * PearlHarborGameConfig.symbolWidth - 8, (symbol.reelTargetPosition + 3) * PearlHarborGameConfig.symbolHeight * 0.5 + 18);

        tl.add([
            TweenLite.to(mulShineSpine.scale, 0.2, {x : 2, y : 2}),
            () => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(mulShineSpine);
                mulShineSpine.alpha = 1;
            }
        ], 0);
        tl.add([
            TweenLite.to(mulShineSpine.position, 0.2, {
                x : PearlHarbor.multiplier.position.x,
                y : PearlHarbor.multiplier.position.y,
                onStart : () => {
                    PearlHarborSoundManager.playMultUpSound();
                },
                onComplete : () => {
                    PearlHarbor.multiplier.value = PearlHarbor.model.gameData.multiplierNextSpin;

                    // #58 start mult from x2
                    if(PearlHarbor.multiplier.value > 1) {
                        PearlHarbor.multiplier.show();

                    }
                }
            }),
            TweenLite.to(mulShineSpine, 0.1, {alpha : 0, delay : 0.1})
        ], 0.2);
        tl.add(() => {mulShineSpine.parent?.removeChild(mulShineSpine);mulShineSpine.destroy();}, 0.9);
        return tl;
    }

    public scatterMultiplierShineFlyAnimation(symbol:PearlHarborSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        if(symbol.sizeMultiplier < 2) {
            return tl;
        }
        const mulShineSpine:BitMapMultiplier = new BitMapMultiplier(PearlHarborGameAssets.BITMAP_FONT_MULTIPLIER, 24, "x");
        mulShineSpine.value = symbol.sizeMultiplier;
        mulShineSpine.alpha = 0;
        mulShineSpine.position.set((symbol.reelId + 0.5) * PearlHarborGameConfig.symbolWidth - 8, (symbol.reelTargetPosition + 3) * PearlHarborGameConfig.symbolHeight * 0.5 + 18);
        tl.add([
            TweenLite.to(mulShineSpine.scale, 0.2, {x : 2, y : 2}),
            () => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(mulShineSpine);
                mulShineSpine.alpha = 1;
            }
        ], 0);
        tl.add([
            TweenLite.to(mulShineSpine.position, 0.2, {
                x : PearlHarbor.scatterCounter.x + (NolimitApplication.isLandscape ? 0 : 68 * 4) + mulShineSpine.width,
                y : PearlHarbor.scatterCounter.y + (NolimitApplication.isLandscape ? 180 : 0) + mulShineSpine.height,
                onStart : () => {
                    PearlHarborSoundManager.playMultUpSound();
                },
                onComplete : () => {
                    PearlHarbor.scatterCounter.value += (symbol.sizeMultiplier > 1 ? symbol.sizeMultiplier : 0);
                }
            }),
            TweenLite.to(mulShineSpine, 0.1, {alpha : 0, delay : 0.1})
        ], 0.2);
        tl.add(() => {mulShineSpine.parent?.removeChild(mulShineSpine);mulShineSpine.destroy();}, 0.9);
        return tl;
    }

    private splitXS(orgSym:PearlHarborSymbol):boolean {
        if(PearlHarborHelper.isXS(orgSym.symbolName)) {
            if(!orgSym.isActivated) {
                (<PearlHarborSplitSymbol>orgSym).addSword();
            }
            return true;
        }
        return false;
    }

    public createNormalSplitSymbols(orgSym:PearlHarborSymbol, afterSplitData:ISymbolData[], index:number):void {
        for(let i:number = 0; i < 2; i++) {
            const symbol:ISymbol = this.createSymbol(afterSplitData[index + i], PearlHarborSymbolStates.NORMAL, {
                reelId : this._reelId,
                startPosition : orgSym.reelTargetPosition + i,
                targetPosition : orgSym.reelTargetPosition + i
            }, index + i, true);
            this._allOutSymbols.push(symbol);
            symbol.alpha = 0;
            symbol.position.set(orgSym.position.x, orgSym.position.y + PearlHarborGameConfig.symbolHeight * (-0.25 + i * 0.5));
        }
    }

    private isSameBigSym(org:PearlHarborSymbol, newSym:ISymbolData):boolean {
        if(org.topLeftSeed != newSym.topLeftSeed) {
            return false;
        }
        if(org.symSize != newSym.size) {
            return false;
        }
        return true;
    }

    public createBigSplitSymbols(orgSym:PearlHarborSymbol, afterSplitData:ISymbolData[], index:number):number {
        if(this.isSameBigSym(orgSym, afterSplitData[index])) {
            this._allOutSymbols.push(orgSym);
            return 1;
        }

        if(afterSplitData[index].isSplit) {
            this.createNormalSplitSymbols(orgSym, afterSplitData, index);
            this._beforeSplitSymbol.push(orgSym);
            return 2;
        }
        const symbol:ISymbol = this.createSymbol(afterSplitData[index], PearlHarborSymbolStates.NORMAL, {
            reelId : this._reelId,
            startPosition : orgSym.reelTargetPosition,
            targetPosition : orgSym.reelTargetPosition
        }, index, true);
        symbol.alpha = 0;
        this._beforeSplitSymbol.push(orgSym);
        this._allOutSymbols.push(symbol);
        return 1;
    }

    public replaceSplitSymbols():void {
        const symbolsLeft:ISymbol[] = ArrayHelper.initArrayWithValues(this._allOutSymbols.length, (i:number) => this._allOutSymbols[i]),
            afterSplitData:ISymbolData[] = ArrayHelper.initArrayWithValues(PearlHarbor.model.afterSplitSymbolsData[this._reelId].length, (i:number) => {
                return PearlHarbor.model.afterSplitSymbolsData[this._reelId][i]
            }),
            symbolNum:number = symbolsLeft.length;
        this._allOutSymbols = [];
        let onAfterSplitDataId:number = 0;
        for(let i:number = 0; i < symbolNum; i++) {
            let orgSym:PearlHarborSymbol = <PearlHarborSymbol>symbolsLeft[i];
            if(PearlHarborHelper.isXS(orgSym.symbolName)) {
                this._allOutSymbols.push(orgSym);
                onAfterSplitDataId += 1;
            } else if(PearlHarborHelper.isNotSplitSymbol(orgSym.symbolName) || orgSym.isSplit) {
                this._allOutSymbols.push(orgSym);
                onAfterSplitDataId += 1;
            } else {
                if(orgSym.isBig) {
                    onAfterSplitDataId += (this.createBigSplitSymbols(orgSym, afterSplitData, onAfterSplitDataId));
                } else if(!afterSplitData[onAfterSplitDataId].isSplit) {
                    this._allOutSymbols.push(orgSym);
                    onAfterSplitDataId += 1;
                } else {
                    this.createNormalSplitSymbols(orgSym, afterSplitData, onAfterSplitDataId);
                    onAfterSplitDataId += 2;
                    this._beforeSplitSymbol.push(orgSym);
                }
            }
        }
        this.resetSymbolConfig(false);
    }

    public showSplitSymbols():void {
        this._allOutSymbols.forEach((sym:ISymbol, i:number) => {
            sym.alpha = 1;
            if(this.splitXS((<PearlHarborSymbol>sym))) {
                (<PearlHarborSplitSymbol>sym).updateMultiplier();
            } else if(PearlHarborHelper.isNotSplitSymbol(sym.symbolName) || (<PearlHarborSymbol>sym).isSplit) {
                (<PearlHarborSymbol>sym).updateMultiplier(i);
            }
        });
        this._beforeSplitSymbol.forEach((oldSym:ISymbol) => {
            this.removeAvalancheSymbol(oldSym);
        });
    }

    public playSymbolLandingOnRestore():void {
        this._allOutSymbols.forEach((sym:ISymbol) => {
            const symbol = <PearlHarborSymbol>sym;
            if(symbol.symbolName === SYMBOL_NAME.TARGET || symbol.symbolName === SYMBOL_NAME.TARGET_H) {
                (<PearlHarborTargetSymbol>symbol).playTargetLandingAnim();
            } else if(symbol.symbolName.indexOf(SYMBOL_NAME.BOMB) > -1) {
                (<PearlHarborSymbol>symbol).playOutsideIdleAnim();
            } else if(symbol.symbolName.indexOf(SYMBOL_NAME.XS) > -1) {
                (<PearlHarborSplitSymbol>symbol).playXSLandingAnim();
            } else if(symbol.symbolName.indexOf(SYMBOL_NAME.SCATTER) > -1) {
                PearlHarbor.scatterAnimation.addScatter(<PearlHarborScatterSymbol>symbol);
            }
        });
    }

    public setSymbolActiveState():void {
        const targetArea:ITargetArea = PearlHarbor.activeSymbolsBackground.targetArea,
            rowStart:number = targetArea.rowStart,
            rowEnd:number = targetArea.rowEnd;
        if(this._reelId < targetArea.reelStart || this._reelId > targetArea.reelEnd) {
            this._allOutSymbols.forEach((sym:ISymbol) => {
                (<PearlHarborSymbol>sym).isActivated = false;
            });
        } else {
            this._allOutSymbols.forEach((sym:ISymbol) => {
                if((sym.reelTargetPosition) >= rowStart && sym.reelTargetPosition <= rowEnd) {
                    if(sym.symbolName.indexOf("BN") > -1 && !(<PearlHarborSymbol>sym).isActivated) {
                        (<PearlHarborSymbol>sym).isActivated = true;
                        PearlHarbor.scatterCounter.enableCounter();
                    }
                    (<PearlHarborSymbol>sym).isActivated = true;
                } else {
                    (<PearlHarborSymbol>sym).isActivated = false;
                }
            });
        }
    }

    public stopSymbolIdleAnimation():void {
        this._allOutSymbols.forEach((sym:ISymbol) => {
            (<PearlHarborSymbol>sym).removeIdleAnimation();
        });
    }

    public getAllSymbols():ISymbol[] {
        return this._allOutSymbols;
    }

    public get bigSymbolView():PIXI.Container {
        return this.bigSymolView;
    }

    get specialSymbolContainer():PIXI.Container {
        return this.specialSymbolView;
    }
}